import { useContext, useEffect, useState } from "react";
import { AppContext } from "@/App";
import api from "@/lib/api";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import Header from "@/components/Header";
import BookingWidget from "@/components/BookingWidget";
import Services from "@/components/Services";
import Fleet from "@/components/Fleet";
import Trust from "@/components/Trust";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import HowItWorks from "@/components/HowItWorks";
import ChatWidget from "@/components/ChatWidget";
import Faq from "@/components/Faq";
import { ShieldCheck, Clock, Plane, BadgeCheck, MessageCircle } from "lucide-react";

const TRUST_ICONS = [BadgeCheck, Clock, Plane, ShieldCheck];
const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Landing() {
  const { t, lang } = useContext(AppContext);
  const [rates, setRates] = useState(null);

  useEffect(() => {
    api.get("/pricing").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  const routeChips = rates
    ? [
        ...rates.flat_routes.map((r) => ({
          label: r.label,
          price: `${lang === "it" ? "a partire da" : "from"} €${r.price % 1 === 0 ? r.price.toFixed(0) : r.price}`,
        })),
        t.popular.routes[t.popular.routes.length - 1],
      ]
    : t.popular.routes;
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 900], [0, 220]);
  const heroOpacity = useTransform(scrollY, [0, 700], [1, 0.35]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.12, anchors: true });
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div id="top" className="min-h-screen bg-[#0B0C0E]">
      <Header />

      <section className="relative flex min-h-screen items-center overflow-hidden">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1645211710746-9629755e6814?auto=format&fit=crop&w=1920&q=60"
            alt=""
            fetchPriority="high"
            className="h-[120%] w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0E]/70 via-[#0B0C0E]/55 to-[#0B0C0E]" />
        </motion.div>

        <div className="relative mx-auto grid w-full max-w-7xl gap-12 px-4 pb-24 pt-32 sm:px-6 lg:grid-cols-2 lg:items-center">
          <div>
            <motion.p
              className="label-accent"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              {t.hero.badge}
            </motion.p>
            <motion.div
              className="my-6 h-px w-28 origin-left bg-[#D4AF37]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.2, delay: 0.25, ease: EASE }}
            />
            <h1 className="font-display text-5xl font-medium leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
              <MaskedLine delay={0.35}>{t.hero.titleA}</MaskedLine>
              <MaskedLine delay={0.5}>
                <span className="italic text-[#E5C158]">{t.hero.titleB}</span>
              </MaskedLine>
            </h1>
            <motion.p
              className="mt-6 max-w-md text-base leading-relaxed text-neutral-300 sm:text-lg"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            >
              {t.hero.sub}
            </motion.p>
            <motion.a
              href="#prenota"
              data-testid="hero-booking-cta-btn"
              className="btn-gold mt-8"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              {t.hero.cta}
            </motion.a>
            <motion.div
              className="mt-12 grid grid-cols-2 gap-4 sm:max-w-md"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.1 }}
            >
              {t.hero.trust.map((item, i) => {
                const Icon = TRUST_ICONS[i];
                return (
                  <div key={item} className="flex items-center gap-2.5 text-xs text-neutral-400">
                    <Icon className="h-4 w-4 shrink-0 text-[#D4AF37]" />
                    {item}
                  </div>
                );
              })}
            </motion.div>
          </div>

          <motion.div
            id="prenota"
            className="scroll-mt-28"
            initial={{ opacity: 0, y: 64 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1, ease: EASE }}
          >
            <BookingWidget />
          </motion.div>
        </div>
      </section>

      <Marquee items={routeChips.map((r) => r.label)} />

      <Reveal><HowItWorks /></Reveal>

      <section id="tariffe" className="bg-[#0D0F12]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <Reveal>
            <p className="label-accent mb-6">{t.popular.title}</p>
            <div className="flex flex-wrap gap-3">
              {routeChips.map((r) => (
                <div
                  key={r.label}
                  className="flex items-center gap-3 rounded-full border border-[rgba(212,175,55,0.2)] bg-[#131519] px-5 py-2.5 transition-colors duration-300 hover:border-[rgba(212,175,55,0.5)]"
                >
                  <span className="text-sm text-neutral-300">{r.label}</span>
                  <span className="font-code text-sm font-medium text-[#E5C158]">{r.price}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <Reveal><Services /></Reveal>
      <Reveal><Fleet /></Reveal>
      <Reveal><Trust /></Reveal>
      <Reveal><Faq /></Reveal>
      <Footer />

      <motion.a
        href="https://wa.me/393482215105"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-floating-btn"
        aria-label="WhatsApp"
        className="fixed bottom-6 left-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-[0_8px_30px_rgba(37,211,102,0.4)]"
        whileHover={{ scale: 1.12, rotate: 6 }}
        whileTap={{ scale: 0.94 }}
      >
        <MessageCircle className="h-7 w-7 text-white" />
      </motion.a>
      <ChatWidget />
    </div>
  );
}
